import { useState, useCallback, useRef, type CSSProperties } from 'react';
import styled from '@emotion/styled';
import { ClickAwayListener } from '@mui/base';
import { CalendarDots } from '@phosphor-icons/react';
import type { Dayjs } from 'dayjs';
import { formatDateTime } from 'utils';
import { Text } from 'components';
import { TableTooltip } from './TableTooltip';
import { useEscapeToClose, useEnterToClose, useClickOutside, useFocusOnEdit } from '../hooks';
import { useEditableContextOptional } from './OptimizedTableProvider';
import { clearEditOnSuccess } from '../utils/inlineEditUtils';
import { StyledDatePicker } from '../styles/tableStyles';
import type { DatePickerCellProps } from '../types';
import {
  DATE_CELL_BASE,
  DATE_CELL_HOVER,
  DATE_CELL_EDITING,
  DATE_CELL_ERROR,
  DATE_CELL_ERROR_TEXT,
  DATE_CELL_DISABLED,
  DATE_CELL_DISABLED_TEXT,
  DATE_PLACEHOLDER_STYLE,
} from './dateInlineEditStyles';

/** Default date format */
export const defaultDateFormat = 'dd-LLL-yyyy';

/** Selector for DatePicker popover so we can ignore clicks on the calendar when closing */
const CALENDAR_POPOVER_SELECTOR = '[data-testid="calendar-popover"]';

/**
 * DatePickerCell - A self-contained inline-editable date cell for tables
 *
 * Features:
 * - Click to edit with DatePicker
 * - Three-dots menu with Edit option
 * - Validation and error display
 * - Optional tooltip
 * - Configurable date format
 * - Visual states: hover, editing, error, disabled (per Figma dl-59431)
 * - No dependencies on legacy EditableCell component (bypasses its problematic useEffect)
 *
 * @example
 * ```tsx
 * <DatePickerCell
 *   context={context}
 *   value={row.deadline}
 *   onUpdate={(date) => updateItem(row.id, { deadline: date })}
 *   editableRowIndex={editableRowIndex}
 *   editableColumnId={editableColumnId}
 *   tooltipContent={<span>Created: {row.createdDate}</span>}
 * />
 * ```
 */
export const DatePickerCell = <T extends { id: number | string }>({
  context,
  value,
  onUpdate,
  editableRowIndex: editableRowIndexProp,
  editableColumnId: editableColumnIdProp,
  dateFormat = defaultDateFormat,
  placeholder = '',
  tooltipContent,
  onCellEdit: onCellEditProp,
  onClickAway: onClickAwayProp,
  error: errorProp,
  disabled: disabledProp,
}: DatePickerCellProps<T>) => {
  const editableCtx = useEditableContextOptional();
  const editableState = editableCtx?.editableState ?? null;
  const setEditableState = editableCtx?.setEditableState ?? ((): void => undefined);

  const editableRowIndex = editableRowIndexProp ?? editableState?.rowIndex ?? null;
  const editableColumnId = editableColumnIdProp ?? editableState?.columnId ?? null;
  const isEditing =
    context.row.index === editableRowIndex && context.column.id === editableColumnId;

  const onCellEdit =
    onCellEditProp ??
    (() => setEditableState({ rowIndex: context.row.index, columnId: context.column.id }));
  const onClickAway =
    onClickAwayProp ?? (() => setEditableState({ rowIndex: null, columnId: null }));

  const [isValid, setIsValid] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const editorContainerRef = useRef<HTMLDivElement>(null);

  useEscapeToClose(isEditing, onClickAway);
  useEnterToClose(isEditing, onClickAway, isValid);

  // Close calendar/editor when clicking outside (including inside the table).
  // ClickAwayListener alone often only closes when clicking outside the table container
  // because the calendar popover is in a portal.
  const handleClickOutside = useCallback(() => {
    if (isValid) onClickAway?.();
  }, [isValid, onClickAway]);
  useClickOutside(editorContainerRef, handleClickOutside, isEditing, CALENDAR_POPOVER_SELECTOR);

  // Focus the date input when entering edit mode so the user can type immediately
  // (calendar opens beside the cell; input stays focusable via Popover disableAutoFocus)
  useFocusOnEdit(editorContainerRef, isEditing, 'input', true);

  const formattedValue = value ? formatDateTime(value.toString(), dateFormat) : '';

  const handleClick = useCallback(() => {
    if (!isEditing && !disabledProp && context.row.original.id !== -1) {
      onCellEdit?.(context);
    }
  }, [isEditing, disabledProp, context, onCellEdit]);

  const handleClickAway = useCallback(() => {
    if (isValid) {
      onClickAway?.();
    }
  }, [isValid, onClickAway]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const handleDateChange = (
    newValue?: string,
    stringDate?: string,
    isValidDate: boolean = true
  ) => {
    setIsValid(isValidDate);
    if (isValidDate && stringDate) {
      const newDate = formatDateTime(stringDate, dateFormat);
      if (newDate !== formattedValue) {
        const result = onUpdate(newDate);
        clearEditOnSuccess(result ?? undefined, onClickAway);
      }
    }
  };

  const hasError = errorProp || !isValid;
  const showHover = isHovered && !isEditing && !disabledProp;

  const cellStateStyle: CSSProperties = {
    ...DATE_CELL_BASE,
    ...(showHover && !hasError ? DATE_CELL_HOVER : {}),
    ...(showHover && hasError
      ? {
          backgroundColor: DATE_CELL_HOVER.backgroundColor,
          cursor: DATE_CELL_HOVER.cursor,
        }
      : {}),
    ...(disabledProp ? DATE_CELL_DISABLED : {}),
    ...(isEditing ? DATE_CELL_EDITING : {}),
    ...(hasError && !isEditing ? DATE_CELL_ERROR : {}),
  };

  const textStyle: CSSProperties = {
    ...(hasError ? DATE_CELL_ERROR_TEXT : {}),
    ...(disabledProp ? DATE_CELL_DISABLED_TEXT : {}),
  };

  // Read mode content — underscore placeholder for empty editable cells
  const readModeContent = formattedValue ? (
    <Text weight="regular" variant="bodyXs" style={textStyle}>
      {formattedValue}
    </Text>
  ) : (
    <span style={{ ...DATE_PLACEHOLDER_STYLE, ...textStyle }}>_</span>
  );

  // Show calendar icon on hover/focus only (not editing, not disabled, not error)
  const showCalendarIcon = showHover && !hasError;

  return (
    <CellWrapper
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={cellStateStyle}
    >
      <CellContainer
        onClick={handleClick}
        tooltipTitle={isEditing ? undefined : tooltipContent || formattedValue}
      >
        {isEditing ? (
          <ClickAwayListener onClickAway={handleClickAway}>
            <EditorContainer ref={editorContainerRef}>
              <StyledDatePicker
                autoOpen
                calendarPosition="right"
                errorIcon={false}
                formatErrorHelperText=""
                id={`date-picker-${context.column.id}`}
                placeholder={placeholder}
                hideClear
                initialDate={formattedValue}
                onCalendarClose={() => {
                  if (isValid) onClickAway?.();
                }}
                onBlur={(date: Dayjs | null, stringDate: string, error: boolean) => {
                  handleDateChange(date?.toISOString() ?? undefined, stringDate, !error);
                  if (!error) {
                    onClickAway?.();
                  }
                }}
                onChange={(
                  _event: React.SyntheticEvent,
                  date: Dayjs | null,
                  reason: string,
                  stringDate: string,
                  _error: boolean
                ) => {
                  if (reason === 'reset' && date) {
                    handleDateChange(date.toISOString(), stringDate);
                  }
                }}
              />
            </EditorContainer>
          </ClickAwayListener>
        ) : (
          readModeContent
        )}
      </CellContainer>
      {showCalendarIcon && (
        <CalendarIconWrapper>
          <CalendarDots size={20} weight="light" />
        </CalendarIconWrapper>
      )}
    </CellWrapper>
  );
};

// ============================================================================
// Styled Components
// ============================================================================

const CellWrapper = styled.div``;

const CalendarIconWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: ${p => p.theme.palette.fg.primary};
`;

const EditorContainer = styled.div`
  width: 100%;
  height: 32px;
  display: flex;
  align-items: center;

  && .MuiFormControl-root {
    width: 100%;
    flex-direction: row;
    gap: 0;
  }

  && .MuiInputBase-root {
    font-size: 0.75rem;
    width: 100%;
    padding: 0;

    &::before {
      border-bottom: 0.5px solid ${p => p.theme.palette.border.primarySubtle};
    }

    &::after {
      display: none;
    }

    input {
      padding: 0;
      width: 100%;
      box-sizing: border-box;
    }
  }

  .MuiInputAdornment-root {
    display: none;
  }

  .MuiFormHelperText-root {
    display: none;
  }
`;

const CellContentContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  flex: 1;
  min-width: 0;
  overflow: hidden;

  & > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const CellContainer = ({
  children,
  tooltipTitle,
  onClick,
}: {
  children: React.ReactNode;
  tooltipTitle?: React.ReactNode;
  onClick?: () => void;
}) => {
  return tooltipTitle ? (
    <TableTooltip title={tooltipTitle}>
      <CellContentContainer onClick={onClick}>{children}</CellContentContainer>
    </TableTooltip>
  ) : (
    <CellContentContainer onClick={onClick}>{children}</CellContentContainer>
  );
};
